import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { ChevronDown } from "lucide-react";

interface SelectContextType {
  value: string;
  onValueChange: (v: string) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
  registerLabel: (value: string, label: string) => void;
  getLabel: (value: string) => string;
}

const SelectCtx = createContext<SelectContextType>({
  value: "",
  onValueChange: () => {},
  open: false,
  setOpen: () => {},
  registerLabel: () => {},
  getLabel: (v) => v,
});

export function Select({
  value,
  onValueChange,
  children,
}: {
  value: string;
  onValueChange: (v: string) => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const labels = useRef<Record<string, string>>({});

  return (
    <SelectCtx.Provider
      value={{
        value,
        onValueChange: (v) => { onValueChange(v); setOpen(false); },
        open,
        setOpen,
        registerLabel: (v, l) => { labels.current[v] = l; },
        getLabel: (v) => labels.current[v] ?? v,
      }}
    >
      <div className="relative">{children}</div>
    </SelectCtx.Provider>
  );
}

export function SelectTrigger({ children, className = "" }: { children: ReactNode; className?: string }) {
  const { open, setOpen } = useContext(SelectCtx);
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        // Don't close here — SelectContent handles outside click
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => setOpen(!open)}
      className={`flex items-center justify-between w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-green-600 ${className}`}
    >
      {children}
      <ChevronDown className="w-4 h-4 text-gray-400 ml-2 shrink-0" />
    </button>
  );
}

export function SelectValue({ placeholder }: { placeholder?: string }) {
  const { value, getLabel } = useContext(SelectCtx);
  return <span className="truncate">{value ? getLabel(value) : (placeholder ?? "Select...")}</span>;
}

export function SelectContent({ children }: { children: ReactNode }) {
  const { open, setOpen } = useContext(SelectCtx);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    // Delay so the trigger click doesn't immediately re-close
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open, setOpen]);

  if (!open) return null;

  return (
    <div
      ref={ref}
      className="absolute z-50 mt-1 w-full min-w-[8rem] rounded-md border border-gray-200 bg-white shadow-md py-1 overflow-hidden"
    >
      {children}
    </div>
  );
}

export function SelectItem({ value, children }: { value: string; children: ReactNode }) {
  const { value: selected, onValueChange, registerLabel } = useContext(SelectCtx);

  // Register label synchronously during render (safe — only writes to a ref)
  if (typeof children === "string") {
    registerLabel(value, children);
  }

  return (
    <button
      type="button"
      onClick={() => onValueChange(value)}
      className={`w-full text-left px-3 py-1.5 text-sm transition-colors hover:bg-gray-50 ${
        selected === value ? "bg-green-50 text-green-700 font-medium" : "text-gray-900"
      }`}
    >
      {children}
    </button>
  );
}
